import { Box, CommonDataProps, styled, Tooltip } from '@chronosphereio/chrono-ui';
import { DATA_TRACK_IDS } from '@chronosphereio/core';
import { useOptimisticState } from '@chronosphereio/shared';
import { Refresh as RefreshIcon } from 'mdi-material-ui';
import { useTimeRange } from '@/plugin-system/runtime';
import { NamedTimeOption } from '../../core';
import { CombinedControls, CombinedControlsIconButton } from '../CombinedControls';
import { useTimeZone } from '../context';
import { DateTimeRangePicker, TimeOption } from '../DateTimeRangePicker';
import { RefreshIntervalPicker } from '../RefreshIntervalPicker';
import {
  getResponsiveTimeRangeSelectMinWidth,
  useResponsiveTimeRangeControls,
} from '../utils/responsiveTimeRangeUtils';
import { TIME_RANGE_CONTROL_LABELS } from './model';
import { PinButton } from './PinButton';
import { TimeRangeWindowControls } from './TimeRangeWindowControls';

export interface TimeRangeControlsProps extends CommonDataProps {
  /**
   * Whether to show the time range selector
   * @default true
   */
  showTimeRangeSelector?: boolean;
  /**
   * Whether to show the refresh button
   * @default true
   */
  showRefreshButton?: boolean;
  /**
   * Whether to show the refresh interval selector
   * @default true
   */
  showRefreshInterval?: boolean;

  /**
   * Show the time range window controls (zoom in, zoom out, etc.)
   * @default false
   */
  showTimeRangeWindowControls?: boolean;

  /**
   * The time options shown in the time range selector
   */
  timeOptions?: NamedTimeOption | TimeOption[];
  /**
   * Time zone to format the start and end dates. Defaults to local time if empty.
   * The timeZone should be in the IANA standard format (e.g. "America/New_York").
   * A list of all the IANA timezones can be found here: https://timezonedb.com/time-zones.
   *
   * The DateTimeRangePicker should used in conjunction with an existing time zone provider
   * such as the `useTimeZone` hook within the cloud/viz-framework projects.
   */
  timeZone?: string;

  /**
   * How long to wait before updating the global time range after a local change.
   * This is useful for preventing unnecessary re-renders when the user is actively.
   * You may want to increase or decrease this value depending on the performance of your app.
   * and how many components are listening to the global time range.
   * @default 50
   */
  updateDelayMs?: number;

  /**
   * The maximum date that can be selected in the calendar.
   */
  maxDate?: Date;

  /**
   * The minimum date that can be selected in the calendar.
   */
  minDate?: Date;

  /**
   * Slot for injecting the refresh query button.
   *
   * Strongly recommend using this slot for layout of time range controls when using the new `ResponsivePageActions`
   * component to ensure layouts work as expected.
   */
  runQueryButton?: React.ReactNode;

  /**
   * Slot for injecting additional controls to the left of the time range controls.
   *
   * Note that this slot will only show the controls at larger screen sizes (currently, above the `sm` breakpoint). On
   * smaller screens, if additional controls are still needed, they should be injected into the `filterActions`, so they
   * can be shown in the filter menu, which avoids them taking up significant screen space on small screens.
   *
   * Strongly recommend using this slot for layout of time range controls when using the new `ResponsivePageActions`
   * component to ensure layouts work as expected.
   */
  additionalControlsStart?: React.ReactNode;

  /**
   * When true, the time zone override is disabled.
   * @default false
   */
  disableTimeZoneOverride?: boolean;

  /**
   * An optional test ID that will be added to the root element of the controls. Can be useful if you potentially have
   * more than one instance of `TimeRangeControls` on a page.
   */
  'data-testid'?: CommonDataProps['data-testid'];

  /**
   * An optional tracking ID that will be added to the root element of the controls. Can be useful if you potentially have
   * more than one instance of `TimeRangeControls` on a page.
   */
  'data-trackid'?: CommonDataProps['data-trackid'];
}

/**
 * Context-aware component for time range selection. Includes optional refresh button and refresh interval selector.
 *
 * Built-in responsive behavior in the time range controls assumes you have configured an element-based breakpoint
 * provider using the responsive utilities from chrono-ui. Otherwise, you may see errors.
 */
export function TimeRangeControls({
  showTimeRangeSelector = true,
  showTimeRangeWindowControls: initShowTimeRangeWindowControls = false,
  showRefreshButton = true,
  showRefreshInterval: initShowRefreshInterval = true,
  runQueryButton,
  timeOptions,
  timeZone: timeZoneOverride,
  updateDelayMs = 50,
  'data-testid': dataTestId = 'time-range-controls',
  'data-trackid': dataTrackId = DATA_TRACK_IDS.timeRangeControls.root,
  maxDate,
  minDate,
  additionalControlsStart,
  disableTimeZoneOverride,
}: TimeRangeControlsProps) {
  const {
    timeRange: globalTimeRange,
    setTimeRange: setGlobalTimeRange,
    refresh,
    refreshInterval,
    setRefreshInterval,
    options: { enablePinning },
  } = useTimeRange();
  const timeZone = useTimeZone();
  const [timeRange, setTimeRange] = useOptimisticState(globalTimeRange, setGlobalTimeRange, {
    delayMs: updateDelayMs,
  });

  const { responsiveEnabled, responsiveVariant } = useResponsiveTimeRangeControls();
  const isCompactVariant = responsiveVariant === 'compact';

  const showRefreshInterval = initShowRefreshInterval && !isCompactVariant;
  const showTimeRangeWindowControls = initShowTimeRangeWindowControls && !isCompactVariant;
  const showAdditionalControlsStart = !!additionalControlsStart && !isCompactVariant;

  // Cannot do the responsive width if we have additional controls to the left because it depends on the time range
  // controls being "greedy" in the flexbox layout and this will lead to weird whitespace between it and those
  // additional controls. This is also not safe to use when the responsive feature is not enabled because it is likely
  // to behave strangely in the old page actions.
  const enableResponsiveWidth = responsiveEnabled && !showAdditionalControlsStart;

  // Flex-basis helps us ensure that the flexbox forces wrapping to the next line to take up more space when needed.
  // This helps us keep the time range controls on the first line when it can fit, but ensure it moves to the next line
  // to give enough space in cases where it would be too small on the first line.
  let responsiveFlexBasisValue = getResponsiveTimeRangeSelectMinWidth(timeRange);
  if (showRefreshButton || !!runQueryButton) {
    // Extra space for the buttons to the right of the time range controls.
    responsiveFlexBasisValue += 40;
  }
  if (enablePinning) {
    responsiveFlexBasisValue += 40;
  }
  const flexBasis = enableResponsiveWidth ? `min(${responsiveFlexBasisValue}px, 100%)` : 'auto';

  return (
    <TimeRangeControlsRoot
      sx={{ flex: `1 0 ${flexBasis}`, alignItems: 'center' }}
      data-testid={dataTestId}
      data-trackid={dataTrackId}
    >
      {showAdditionalControlsStart && additionalControlsStart}
      <CombinedControls
        // When responsive width is enabled, we want the time range controls to fill the space greedily while
        // laying out the content to the right. This enables some responsive layout.
        flex={enableResponsiveWidth ? '1' : undefined}
        justifyContent={enableResponsiveWidth ? 'flex-end' : undefined}
      >
        {showTimeRangeSelector && (
          <DateTimeRangePicker
            timeRange={timeRange}
            onChange={setTimeRange}
            timeOptions={timeOptions}
            timeZone={timeZoneOverride ?? timeZone.timeZone}
            userTimeZone={timeZone.userTimeZone}
            setTimeZone={timeZone.setTimeZone}
            maxDate={maxDate}
            minDate={minDate}
            enableResponsiveWidth={enableResponsiveWidth}
            disableTimeZoneOverride={disableTimeZoneOverride}
          />
        )}
        {enablePinning && <PinButton />}
        {showTimeRangeWindowControls && <TimeRangeWindowControls />}
      </CombinedControls>
      {showRefreshButton && (
        <CombinedControls>
          <Tooltip Content={TIME_RANGE_CONTROL_LABELS.REFRESH}>
            <CombinedControlsIconButton
              aria-label={TIME_RANGE_CONTROL_LABELS.REFRESH}
              onClick={refresh}
              id="refresh-button"
            >
              <RefreshIcon />
            </CombinedControlsIconButton>
          </Tooltip>
          {showRefreshInterval && <RefreshIntervalPicker value={refreshInterval} onChange={setRefreshInterval} />}
        </CombinedControls>
      )}
      {runQueryButton}
    </TimeRangeControlsRoot>
  );
}

const TimeRangeControlsRoot = styled(Box)(({ theme }) => {
  return {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.spacing(1),
    alignItems: 'start',
    justifyContent: 'flex-end',
  };
});
